import { useState } from 'react'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { buildQRZip } from '@/lib/bulkQRZip'
import type { Building, Room } from '@/types'

export interface BulkQRDownloadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rooms: Room[]
  /** Buildings used by buildQRZip to look up each room's building. */
  buildings: Building[]
  /** Final ZIP filename the anchor download= attribute receives. */
  zipName: string
  /**
   * Base URL for building per-room public URLs. Defaults to
   * window.location.origin — overridable to make tests deterministic.
   */
  appBaseUrl?: string
}

type Variant = 'bare' | 'labeled'

/**
 * Shared dialog for downloading a ZIP of room QR PNGs. Used by both the
 * Exports page (per-building row / per-org "Download all") and the
 * Building detail page.
 *
 * Stateless in terms of data sourcing: callers supply `rooms`, `buildings`,
 * and `zipName`. The dialog handles user choice (labeled/bare), invokes
 * `buildQRZip`, and triggers the browser download via an anchor click.
 */
export function BulkQRDownloadDialog({
  open,
  onOpenChange,
  rooms,
  buildings,
  zipName,
  appBaseUrl,
}: BulkQRDownloadDialogProps) {
  const [variant, setVariant] = useState<Variant>('bare')
  const [submitting, setSubmitting] = useState(false)

  async function handleDownload() {
    setSubmitting(true)
    try {
      const blob = await buildQRZip(rooms, buildings, {
        labeled: variant === 'labeled',
        zipName,
        appBaseUrl: appBaseUrl ?? window.location.origin,
      })
      triggerBrowserDownload(blob, zipName)
      toast.success(`ZIP downloaded (${rooms.length} files)`)
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to build ZIP'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download QR codes</DialogTitle>
          <DialogDescription>
            Choose a format and download a ZIP of QR code PNGs for every room.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-3 py-2" disabled={submitting}>
          <legend className="sr-only">QR variant</legend>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="qr-variant"
              value="bare"
              checked={variant === 'bare'}
              onChange={() => setVariant('bare')}
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-medium">Bare (just the QR)</span>
              <span className="block text-muted-foreground">
                Minimal output — no room or building labels.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="qr-variant"
              value="labeled"
              checked={variant === 'labeled'}
              onChange={() => setVariant('labeled')}
              className="mt-1"
            />
            <span className="text-sm">
              <span className="font-medium">
                Labeled (with room + building name)
              </span>
              <span className="block text-muted-foreground">
                Each PNG includes the room and building name below the QR.
              </span>
            </span>
          </label>
        </fieldset>

        <p className="text-sm text-muted-foreground">
          {rooms.length} QR codes will be downloaded as a ZIP.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={submitting}>
            {submitting ? (
              'Generating...'
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
